import { useEffect, useState } from 'react';
import { abbreviate } from '@chainflip/utils/string';
import assert from 'assert';
import {
  formatDistanceToNow,
  hoursToMilliseconds,
  millisecondsToHours,
  minutesToMilliseconds,
} from 'date-fns';
import { isAddress } from 'viem';
import { useAccount } from 'wagmi';
import { DashboardModalWithCloseButton, ModalContent } from '@/shared/components/dashboards/Modal';
import Callout from '@/shared/components/flip-ui-kit/Callout';
import WalletActionButton from '@/shared/components/WalletActionButton';
import { usePendingRedemption, useRedemptionDelay, useRedemptionTax } from '@/shared/hooks/funding';
import useAuction from '@/shared/hooks/useAuction';
import useEvmScAccount from '@/shared/hooks/useEvmScAccount';
import useEvmScCall from '@/shared/hooks/useEvmScCall';
import useSteps from '@/shared/hooks/useSteps';
import useToast from '@/shared/hooks/useToast';
import { chainflipAssetMap, FLIP_SYMBOL } from '@/shared/utils/env';
import { getViemError } from '@/shared/utils/functions';
import TokenAmount from '@/shared/utils/TokenAmount';
import { StartView, ThresholdSignatureView, FinalizeView } from './Views';

const RedemptionModal = ({ open, onClose }: { open: boolean; onClose: VoidFunction }) => {
  const {
    idSs58: delegatorIdSs58,
    flipBalance,
    redeemableBalance,
    delegationSDK: sdk,
    nextEpoch,
    currentEpoch,
  } = useEvmScAccount(true);
  const evmAccount = useAccount();
  const { auction } = useAuction();
  const { step, stepId, stepCount, advanceStep, resetSteps, gotoStep } = useSteps(
    'Redeeming Funds',
    'Waiting for request to be registered...',
    'Finalize Withdrawal',
  );

  const [refetchInterval, setRefetchInterval] = useState<number>();

  const { data: pendingRedemption = null } = usePendingRedemption(delegatorIdSs58, {
    refetchInterval,
  });

  const [address, setAddress] = useState(evmAccount.address ?? '');
  const [redeemAmount, setRedeemAmount] = useState(
    redeemableBalance.gt(0) ? redeemableBalance : undefined,
  );
  const [error, setError] = useState<string>();
  const [isMax, setIsMax] = useState(false);
  const evmToast = useToast('redemption-modal-evm');
  const scToast = useToast('redemption-modal-sc');

  useEffect(() => {
    if (redeemAmount && flipBalance) {
      setIsMax(redeemAmount.eq(flipBalance));
    }
  }, [redeemAmount, flipBalance]);

  const { data: delay = hoursToMilliseconds(48) } = useRedemptionDelay();
  const { data: tax = BigInt(5e18) } = useRedemptionTax();

  useEffect(() => {
    if (open) setRedeemAmount(redeemableBalance);
  }, [open]);

  useEffect(() => {
    if (open && pendingRedemption) gotoStep('Finalize Withdrawal');
    if (pendingRedemption) setRefetchInterval(undefined);
  }, [open, pendingRedemption]);

  const reset = () => {
    setAddress(evmAccount.address ?? '');
    setRedeemAmount(undefined);
    setError(undefined);
    setIsMax(false);
    resetSteps();
  };

  const { mutate: submitRedemption, isPending: isSubmittingRedemption } = useEvmScCall({
    fn: async (amount: TokenAmount | undefined) => {
      assert(sdk, 'SDK is not initialized');
      evmToast.loading(
        `Requesting redemption for ${amount?.toFormatted() ?? flipBalance?.toFormatted()} ${FLIP_SYMBOL} to ${abbreviate(address)}`,
      );
      return sdk.redeem(amount);
    },
    onSubmitted: () => {
      evmToast.success('Redemption request submitted successfully');
      scToast.loading('Waiting for redemption request to be processed on the State Chain...');
    },
    onEvmError: (err) => {
      evmToast.error('Failed to submit redemption request', {
        description: getViemError(err),
      });
    },
    onSuccess: () => {
      scToast.success('Redemption request submitted successfully');
      setRefetchInterval(5_000);
      advanceStep();
    },
    onScError: (err) => {
      scToast.error('Redemption request could not be processed by State Chain', {
        description: err.name,
      });
    },
  });

  const { data: redemption = null } = usePendingRedemption(delegatorIdSs58);

  let onClick;
  let buttonText;
  let description;
  let disabled;

  if (step === 'Redeeming Funds') {
    onClick = () => {
      setError(undefined);
      if (!flipBalance) return;
      if (!isAddress(address)) return;
      if (!redeemAmount || redeemAmount.eq(0)) {
        setError('Please enter an amount to redeem');
        return;
      }
      if (redeemableBalance && redeemAmount.gt(redeemableBalance)) return;
      setError(undefined);

      const redeemAll = isMax || redeemAmount.eq(redeemableBalance);

      const afterTax = flipBalance.sub(redeemAmount).sub(tax.toString());
      if (!redeemAll && afterTax.lt(0)) {
        setError('Withdrawal amount too high to pay redemption fee');
        return;
      }
      if (!redeemAll && afterTax.lt(1e18)) {
        setError(`Your account must have 1 ${FLIP_SYMBOL} to remain active`);
        return;
      }

      submitRedemption(redeemAll ? undefined : redeemAmount);
    };
    buttonText = 'Submit request';
  } else if (step === 'Waiting for request to be registered...') {
    description = (
      <>
        The State Chain is submitting your redemption request to the State Chain Gateway. This
        should usually take a minute or two, but in some circumstances might take longer.
      </>
    );
  } else if (step === 'Finalize Withdrawal') {
    buttonText = 'Return to Dashboard';
    onClick = onClose;
    description = (
      <>
        For security reasons, you must wait {millisecondsToHours(delay)} hours to finalise your
        withdrawal. If you don&apos;t finish the process then, your FLIP will be credited back to
        your account balance, and you will need to restart the process.
      </>
    );
  }

  const pendingUndelegation = currentEpoch.delegatedBalance.sub(nextEpoch.delegatedBalance);

  const waitTill = auction
    ? formatDistanceToNow(auction.endsAt.valueOf() + minutesToMilliseconds(20))
    : 'until after the end of the current auction';

  return (
    <DashboardModalWithCloseButton onClose={onClose} open={open} afterLeave={reset}>
      <ModalContent
        step={{ current: stepId, max: stepCount }}
        description={description}
        title={step}
        titleSize="large"
        buttons={
          buttonText && (
            <WalletActionButton
              chainId={chainflipAssetMap.Flip.chain.id}
              requireConnection
              fullWidth
              onClick={onClick}
              loading={isSubmittingRedemption}
              disabled={disabled || 'ifLoading'}
            >
              {buttonText}
            </WalletActionButton>
          )
        }
      >
        <div className="flex w-full flex-col items-center gap-6">
          {pendingUndelegation.gt(0) && (
            <Callout type="info" fullWidth>
              You have {pendingUndelegation.toFormatted()} {FLIP_SYMBOL} pending undelegation. You
              can withdraw this {FLIP_SYMBOL} in {waitTill} after the next epoch starts.
            </Callout>
          )}
          {step === 'Redeeming Funds' && (
            <StartView
              tokenError={error}
              address={address}
              setAddress={setAddress}
              redeemAmount={redeemAmount}
              setRedeemAmount={setRedeemAmount}
              flipBalance={flipBalance ?? TokenAmount.ZERO}
              redeemableBalance={redeemableBalance ?? TokenAmount.ZERO}
              type="delegator"
              onClickMax={() => setIsMax(true)}
            />
          )}
          {step === 'Waiting for request to be registered...' && <ThresholdSignatureView />}
          {step === 'Finalize Withdrawal' && <FinalizeView redemption={redemption!} />}
        </div>
      </ModalContent>
    </DashboardModalWithCloseButton>
  );
};

export default RedemptionModal;
